/**
 * Users API service
 * Handles all user account-related API calls
 */

import { apiFetch } from './index';

export interface UserAccount {
  userId: number;
  authProviderId?: string;
  personId: number;
  roleId: number;
  isActive: boolean;
  lastLoginAt?: string;
  person: {
    personId: number;
    firstName: string;
    middleName?: string;
    lastSurname: string;
    preferredName?: string;
    email?: string;
  };
  role: {
    roleId: number;
    roleName: string;
    roleDescription: string;
  };
  permissions?: string[];
  organizationAccess?: Array<{
    organizationId: number;
    organizationName: string;
  }>;
  createdAt: string;
  modifiedAt?: string;
}

export interface UserCreateData {
  personId: number;
  roleId: number;
  authProviderId?: string;
  isActive?: boolean;
}

export interface UserUpdateData {
  roleId?: number;
  isActive?: boolean;
}

export interface UserRoleChangeRequest {
  roleId: number;
  reason?: string;
}

export interface BulkUserAction {
  userIds: number[];
  action: 'activate' | 'deactivate' | 'delete';
  reason?: string;
}

export interface UserListResponse {
  users: UserAccount[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export const usersApi = {
  // List users with pagination and filtering
  async list(params?: {
    page?: number;
    pageSize?: number;
    roleId?: number;
    isActive?: boolean;
    search?: string;
    hasLoggedIn?: boolean;
    roleTag?: string;
  }): Promise<UserListResponse> {
    const queryParams = new URLSearchParams();
    queryParams.append('page', (params?.page || 1).toString());
    queryParams.append('pageSize', (params?.pageSize || 50).toString());

    if (params?.roleId !== undefined) {
      queryParams.append('roleId', params.roleId.toString());
    }
    if (params?.isActive !== undefined) {
      queryParams.append('isActive', params.isActive.toString());
    }
    if (params?.search) {
      queryParams.append('search', params.search);
    }
    if (params?.hasLoggedIn !== undefined) {
      queryParams.append('hasLoggedIn', params.hasLoggedIn.toString());
    }
    if (params?.roleTag) {
      queryParams.append('roleTag', params.roleTag);
    }

    // apiFetch already handles errors and returns parsed JSON
    return await apiFetch<UserListResponse>(
      `/users/?${queryParams}`
    );
  },

  // Get a single user by ID
  async get(userId: number): Promise<UserAccount> {
    // apiFetch already handles errors and returns parsed JSON
    return await apiFetch<UserAccount>(`/users/${userId}`);
  },

  // Create a new user account
  async create(data: UserCreateData): Promise<UserAccount> {
    // apiFetch already handles errors and returns parsed JSON
    return await apiFetch<UserAccount>('/users/', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  // Update a user account
  async update(userId: number, data: UserUpdateData): Promise<UserAccount> {
    // apiFetch already handles errors and returns parsed JSON
    return await apiFetch<UserAccount>(`/users/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  },

  // Delete a user account
  async delete(userId: number): Promise<void> {
    // apiFetch already handles errors and returns parsed JSON
    await apiFetch<void>(`/users/${userId}`, {
      method: 'DELETE'
    });
  },

  // Activate a user account
  async activate(userId: number): Promise<{ message: string }> {
    // apiFetch already handles errors and returns parsed JSON
    return await apiFetch<{ message: string }>(`/users/${userId}/activate`, {
      method: 'PATCH'
    });
  },

  // Deactivate a user account
  async deactivate(userId: number): Promise<{ message: string }> {
    // apiFetch already handles errors and returns parsed JSON
    return await apiFetch<{ message: string }>(`/users/${userId}/deactivate`, {
      method: 'PATCH'
    });
  },

  // Change user role
  async changeRole(
    userId: number,
    data: UserRoleChangeRequest
  ): Promise<{ message: string; user: UserAccount }> {
    // apiFetch already handles errors and returns parsed JSON
    return await apiFetch<{ message: string; user: UserAccount }>(
      `/users/${userId}/change-role`,
      {
        method: 'POST',
        body: JSON.stringify(data)
      }
    );
  },

  // Bulk action on multiple users
  async bulkAction(
    data: BulkUserAction
  ): Promise<{
    message: string;
    successCount: number;
    failedCount: number;
  }> {
    // apiFetch already handles errors and returns parsed JSON
    return await apiFetch<{
      message: string;
      successCount: number;
      failedCount: number;
    }>('/users/bulk-action', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }
};